import { useEffect } from "react";

export const RELOAD_COUNTDOWN_MS = 30_000;
export const VERSION_CHECK_INTERVAL_MS = 2 * 60_000;
export const VERSION_FOCUS_CHECK_MIN_GAP_MS = 60_000;

export type DeployRefresh =
  | { kind: "current" }
  | { kind: "stale"; deployedId: string }
  | { kind: "countdown"; deployedId: string; reloadAt: number }
  | { kind: "declined"; deployedId: string }
  | { kind: "hidden"; deployedId: string; from: "stale" | "declined" };

export type DeployRefreshEvent =
  | { type: "checked"; deployedId: string; tabId: string }
  | { type: "route"; live: boolean; now: number }
  | { type: "navigated" }
  | { type: "dismiss" };

export function nextDeployRefresh(state: DeployRefresh, event: DeployRefreshEvent): DeployRefresh {
  switch (event.type) {
    case "checked":
      if (event.deployedId === event.tabId) return state.kind === "current" ? state : { kind: "current" };
      if (state.kind !== "current" && state.deployedId === event.deployedId) return state;
      return { kind: "stale", deployedId: event.deployedId };
    case "route":
      if (state.kind === "stale" && event.live) {
        return { kind: "countdown", deployedId: state.deployedId, reloadAt: event.now + RELOAD_COUNTDOWN_MS };
      }
      if (state.kind === "countdown" && !event.live) return { kind: "stale", deployedId: state.deployedId };
      return state;
    case "navigated":
      return state.kind === "hidden" ? { kind: state.from, deployedId: state.deployedId } : state;
    case "dismiss":
      if (state.kind === "countdown") return { kind: "declined", deployedId: state.deployedId };
      if (state.kind === "stale" || state.kind === "declined") return { kind: "hidden", deployedId: state.deployedId, from: state.kind };
      return state;
  }
}

const holds = new Set<symbol>();

export function useReloadHold(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const hold = Symbol("reload-hold");
    holds.add(hold);
    return () => {
      holds.delete(hold);
    };
  }, [active]);
}

const TEXT_ENTRY =
  'textarea, select, input:not([type="button"], [type="submit"], [type="reset"], [type="checkbox"], [type="radio"], [type="range"], [type="file"], [type="color"])';

export function hasUnsavedInput(doc: Document): boolean {
  if (holds.size > 0) return true;
  if (doc.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]')) return true;
  const active = doc.activeElement;
  return active instanceof HTMLElement && (active.isContentEditable || active.matches(TEXT_ENTRY));
}
